/**
 * Cart Goal Meter Component
 * Displays progress toward free shipping and/or free gift goals
 * Integrates with Dawn theme's pubsub system for cart updates
 */

(function() {
  'use strict';

  class CartGoalMeter extends HTMLElement {
    constructor() {
      super();
      
      this.cartTotal = 0;
      this.freeShippingGoal = 0;
      this.freeGiftGoal = 0;
      this.freeGiftTitle = 'a FREE gift';
      this.colorScheme = 'green';
      
      // DOM elements
      this.goalMeterText = null;
      this.progressFilled = null;
      this.freeShippingMarker = null;
      this.freeGiftMarker = null;
      this.freeShippingTooltip = null;
      this.freeGiftTooltip = null;
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    static get observedAttributes() {
      return ['data-cart-total', 'data-free-shipping-goal', 'data-free-gift-goal'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue !== newValue) {
        this.loadConfiguration();
        this.updateDisplay();
      }
    }

    init() {
      this.loadConfiguration();
      this.cacheDomElements();
      this.setupEventListeners();
      this.updateDisplay();
    }

    loadConfiguration() {
      this.freeShippingGoal = parseFloat(this.dataset.freeShippingGoal) || 0;
      this.freeGiftGoal = parseFloat(this.dataset.freeGiftGoal) || 0;
      this.freeGiftTitle = this.dataset.freeGiftTitle || 'a FREE gift';
      this.colorScheme = this.dataset.colorScheme || 'green';
      this.cartTotal = parseFloat(this.dataset.cartTotal) || 0;
    }

    cacheDomElements() {
      this.goalMeterText = this.querySelector('.goal-meter-text');
      this.progressFilled = this.querySelector('.progress-filled');
      this.freeShippingMarker = this.querySelector('.free-shipping-marker');
      this.freeGiftMarker = this.querySelector('.free-gift-marker');
      this.freeShippingTooltip = this.querySelector('.free-shipping-tooltip');
      this.freeGiftTooltip = this.querySelector('.free-gift-tooltip');
    }

    setupEventListeners() {
      // No event listeners needed - updates are handled externally
    }

    updateDisplay() {
      // Update cart total in dataset
      this.dataset.cartTotal = this.cartTotal;

      // Update progress bar
      this.updateProgressBar();
      
      // Update goal meter text
      this.updateGoalMeterText();
      
      // Update tooltips
      this.updateTooltips();
      
      // Update marker positions
      this.updateMarkerPositions();
    }

    updateProgressBar() {
      this.progressFilled = this.querySelector('.progress-filled');

      if (!this.progressFilled) return;

      const filledPercentage = this.calculateFilledPercentage();
      const newWidth = `${filledPercentage * 100}%`;
      
      this.progressFilled.style.width = newWidth;
    }

    calculateFilledPercentage() {
      if (!this.freeShippingGoal && !this.freeGiftGoal) {
        return 0;
      }

      let firstGoal = null;
      let secondGoal = null;
      let sameGoal = false;
      let percentage = 0;

      if (this.freeShippingGoal && this.freeGiftGoal) {
        // Check which is higher
        if (this.freeShippingGoal > this.freeGiftGoal) {
          // Free shipping goal is higher
          firstGoal = this.freeGiftGoal;
          secondGoal = this.freeShippingGoal;
        } else if (this.freeGiftGoal > this.freeShippingGoal) {
          // Free gift goal is higher
          firstGoal = this.freeShippingGoal;
          secondGoal = this.freeGiftGoal;
        } else {
          // Both goals are the same
          sameGoal = true;
          secondGoal = this.freeShippingGoal;
        }
      } else if (this.freeShippingGoal) {
        firstGoal = this.freeShippingGoal;
        secondGoal = null;
      } else if (this.freeGiftGoal) {
        firstGoal = this.freeGiftGoal;
        secondGoal = null;
      }

      if (sameGoal) {
        percentage = this.cartTotal / secondGoal;
      } else {
        if (this.cartTotal < firstGoal) {
          percentage = this.cartTotal / firstGoal;
          if (secondGoal) {
            percentage = percentage * 0.5;
          }
        } else {
          percentage = ((this.cartTotal - firstGoal) / secondGoal) + 0.5;
        }
      }

      return Math.min(1, percentage); // Cap at 100%
    }

    updateGoalMeterText() {
      if (!this.goalMeterText) return;

      const text = this.calculateGoalMeterText();
      this.goalMeterText.innerHTML = text;
    }

    calculateGoalMeterText() {
      let currentTarget;
      let remainingAmount;

      if (!this.freeShippingGoal && !this.freeGiftGoal) {
        return '';
      }

      if (this.freeShippingGoal === this.freeGiftGoal) {
        currentTarget = `FREE shipping and ${this.freeGiftTitle}!`;
        if (this.cartTotal < this.freeShippingGoal) {
          remainingAmount = this.freeShippingGoal - this.cartTotal;
        } else {
          return `You've unlocked FREE shipping and ${this.freeGiftTitle}!`;
        }
      } else {
        if (this.freeShippingGoal && this.freeGiftGoal) {
          if (this.freeShippingGoal > this.freeGiftGoal) {
            if (this.cartTotal < this.freeGiftGoal) {
              currentTarget = this.freeGiftTitle;
              remainingAmount = this.freeGiftGoal - this.cartTotal;
            } else if (this.cartTotal < this.freeShippingGoal) {
              currentTarget = 'FREE shipping!';
              remainingAmount = this.freeShippingGoal - this.cartTotal;
            } else {
              return `You've unlocked FREE shipping and ${this.freeGiftTitle}!`;
            }
          } else {
            if (this.cartTotal < this.freeShippingGoal) {
              currentTarget = 'FREE shipping!';
              remainingAmount = this.freeShippingGoal - this.cartTotal;
            } else if (this.cartTotal < this.freeGiftGoal) {
              currentTarget = this.freeGiftTitle;
              remainingAmount = this.freeGiftGoal - this.cartTotal;
            } else {
              return `You've unlocked FREE shipping and ${this.freeGiftTitle}!`;
            }
          }
        } else if (this.freeShippingGoal) {
          if (this.cartTotal < this.freeShippingGoal) {
            currentTarget = 'FREE shipping!';
            remainingAmount = this.freeShippingGoal - this.cartTotal;
          } else {
            return `You've unlocked FREE shipping!`;
          }
        } else {
          if (this.cartTotal < this.freeGiftGoal) {
            currentTarget = this.freeGiftTitle;
            remainingAmount = this.freeGiftGoal - this.cartTotal;
          } else {
            return `You've unlocked ${this.freeGiftTitle}!`;
          }
        }
      }

      return `You're <strong>${this.formatMoney(remainingAmount * 100, true, true)}</strong> away from ${currentTarget}!`;
    }

    updateTooltips() {
      this.updateShippingTooltip();
      this.updateGiftTooltip();
    }

    updateShippingTooltip() {
      if (!this.freeShippingTooltip || !this.freeShippingGoal) return;

      let tooltipText;
      if (this.cartTotal < this.freeShippingGoal) {
        tooltipText = `Spend <strong>${this.formatMoney(this.freeShippingGoal * 100, true, true)}</strong> for FREE shipping.`;
      } else {
        tooltipText = `You've unlocked FREE shipping!`;
      }

      this.freeShippingTooltip.innerHTML = tooltipText;
    }

    updateGiftTooltip() {
      if (!this.freeGiftTooltip || !this.freeGiftGoal) return;

      let tooltipText;
      if (this.cartTotal < this.freeGiftGoal) {
        tooltipText = `Spend <strong>${this.formatMoney(this.freeGiftGoal * 100, true, true)}</strong> for ${this.freeGiftTitle}.`;
      } else {
        tooltipText = `You've unlocked ${this.freeGiftTitle}!`;
      }

      this.freeGiftTooltip.innerHTML = tooltipText;
    }

    updateMarkerPositions() {
      this.updateShippingMarkerPosition();
      this.updateGiftMarkerPosition();
    }

    updateShippingMarkerPosition() {
      if (!this.freeShippingMarker || !this.freeShippingGoal) return;

      const filledPercentage = this.calculateFilledPercentage();
      const position = this.calculateMarkerPosition('shipping', filledPercentage);
      const translateX = position > 95 ? 'translateX(-100%)' : 'translateX(-50%)';
      
      this.freeShippingMarker.style.left = `${position}%`;
      this.freeShippingMarker.style.transform = translateX;

      if (this.freeShippingTooltip) {
        if (position > 95) {
          this.freeShippingTooltip.classList.add('-ms-5');
        } else {
          this.freeShippingTooltip.classList.remove('-ms-5');
        }
      }
    }

    updateGiftMarkerPosition() {
      if (!this.freeGiftMarker || !this.freeGiftGoal) return;

      const filledPercentage = this.calculateFilledPercentage();
      const position = this.calculateMarkerPosition('gift', filledPercentage);
      const translateX = position > 95 ? 'translateX(-100%)' : 'translateX(-50%)';
      
      this.freeGiftMarker.style.left = `${position}%`;
      this.freeGiftMarker.style.transform = translateX;

      if (this.freeGiftTooltip) {
        if (position > 95) {
          this.freeGiftTooltip.classList.add('-ms-5');
        } else {
          this.freeGiftTooltip.classList.remove('-ms-5');
        }
      }
    }

    calculateMarkerPosition(goalType, filledPercentage) {
      if (this.freeShippingGoal && this.freeGiftGoal) {
        if (this.freeShippingGoal > this.freeGiftGoal) {
          // Free shipping is higher - gift at 50%, shipping at 100%
          return goalType === 'gift' ? 50 : 100;
        } else if (this.freeGiftGoal > this.freeShippingGoal) {
          // Free gift is higher - shipping at 50%, gift at 100%
          return goalType === 'shipping' ? 50 : 100;
        } else {
          // Same goals - both at 100%
          return 100;
        }
      } else {
        // Only one goal - at 100%
        return 100;
      }
    }

    formatMoney(cents, suppressZeros = true, suppressCurrency = false) {
      if (typeof suppressZeros === 'undefined') {
        suppressZeros = true;
      }
      if (typeof suppressCurrency === 'undefined') {
        suppressCurrency = false;
      }

      let m = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(cents / 100);

      if (suppressZeros) {
        m = m.replace('.00', '');
      }
      if (!suppressCurrency) {
        m += ' USD';
      }

      return m;
    }

    // Public API methods
    updateCartTotal(newTotal) {
      this.cartTotal = newTotal;
      this.updateDisplay();
    }

    // Force refresh from current cart data
    refreshFromCart() {
      this.loadConfiguration();
      this.updateDisplay();
    }

    // Update cart total and refresh display
    setCartTotal(total) {
      this.dataset.cartTotal = total;
      this.cartTotal = parseFloat(total);
      this.updateDisplay();
    }


    destroy() {
      // Clear DOM references
      this.goalMeterText = null;
      this.progressFilled = null;
      this.freeShippingMarker = null;
      this.freeGiftMarker = null;
      this.freeShippingTooltip = null;
      this.freeGiftTooltip = null;
    }
  }

  // Register the custom element
  customElements.define('cart-goal-meter', CartGoalMeter);

})();

